import React, { useState, useEffect } from 'react';
import { Box, IconButton, useTheme, useMediaQuery, AppBar, Toolbar, Typography, Avatar, Menu, MenuItem } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import Sidebar from './Sidebar';
import { logout } from '../../services/authService';
import userService from '../../services/userService';
import UserDisplay from '../UserDisplay';

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const { accounts, inProgress } = useMsal();
  const [username, setUsername] = useState('User');
  const [userEmail, setUserEmail] = useState(null);

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (accounts.length > 0 && inProgress === InteractionStatus.None) {
      const account = accounts[0];
      const email = account.username || account.idTokenClaims?.email;
      const displayName = account.name || 
        account.idTokenClaims?.name || 
        account.idTokenClaims?.given_name || 
        'User';
      
      setUsername(displayName);
      setUserEmail(email);
      
      if (email && email !== 'User') {
        userService.getUserByEmail(email).then(user => {
          if (user && user.photoUrl) {
            setPhotoUrl(user.photoUrl);
          }
        }).catch(error => {
          console.warn('Could not fetch user photo:', error);
        });
      }
    }
  }, [accounts, inProgress]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    if (photoUrl) {
      URL.revokeObjectURL(photoUrl);
    }
    await logout();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.primary.main
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={() => setOpen(!open)}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            {/* Company Logo */}
            <Box component="img"
              src="/images/pbilogo.jpg"
              alt="PBI Logo"
              sx={{ height: 40, width: 'auto', mr: 2, borderRadius: 1, boxShadow: 1 }}
            />
            <Typography variant="h6" noWrap component="div">
              Pulse Business Innovation Operation Portal
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              onClick={handleMenu}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <UserDisplay email={userEmail} size="small" showName={true} />
            </Box>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Sidebar open={open} setOpen={setOpen} />
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 